import { supabase } from '@/lib/supabase';
import type { MemberSafeAsset, MemberSafeMediaJob, PublicQueueStatusRow } from '@/lib/member/safeAssets';

export interface MemberAssetsSafeParams {
  characterId?: number | null;
  taskIds?: number[] | null;
  assetType?: string | null;
  materialLibraryOnly?: boolean;
  characterPhotoOnly?: boolean;
  statuses?: string[] | null;
}

export async function fetchMemberAssetsSafe(params: MemberAssetsSafeParams = {}): Promise<MemberSafeAsset[]> {
  const { data, error } = await supabase.rpc('get_member_assets_safe', {
    p_character_id: params.characterId ?? null,
    p_task_ids: params.taskIds?.length ? params.taskIds : null,
    p_asset_type: params.assetType ?? null,
    p_material_library_only: params.materialLibraryOnly ?? false,
    p_character_photo_only: params.characterPhotoOnly ?? false,
    p_statuses: params.statuses?.length ? params.statuses : null,
  });
  if (error) throw error;
  return (data ?? []) as MemberSafeAsset[];
}

export async function fetchMemberGenerationJobsSafe(limit = 60): Promise<MemberSafeMediaJob[]> {
  const { data, error } = await supabase.rpc('get_member_generation_jobs_safe', { p_limit: limit });
  if (error) throw error;
  return (data ?? []) as MemberSafeMediaJob[];
}

export async function fetchPublicQueueStatus(): Promise<PublicQueueStatusRow> {
  const { data, error } = await supabase.rpc('get_public_queue_status');
  if (error) throw error;
  const row = (data as PublicQueueStatusRow[] | null)?.[0];
  if (!row) {
    return { status: 'smooth', label: '顺畅', description: '目前生成速度正常，任务会依序完成。' };
  }
  return row;
}

/** 确认入库：忽略 RPC raw 回传，立即重读 safe DTO（Network 仍可能见 raw row → RBAC-4） */
export async function confirmAssetSafe(
  assetId: number,
  params: MemberAssetsSafeParams = {},
): Promise<MemberSafeAsset> {
  const { error } = await supabase.rpc('confirm_asset', { p_asset_id: assetId });
  if (error) throw new Error(error.message || '确认入库失败');
  const rows = await fetchMemberAssetsSafe({
    ...params,
    statuses: params.statuses ?? ['ready', 'pending_review'],
  });
  const found = rows.find((r) => r.asset_id === assetId);
  if (!found) throw new Error('确认后读取素材失败');
  return found;
}
